//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles, Grid } from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import ErrorIcon from '@material-ui/icons/Error';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import BlockIcon from '@material-ui/icons/Block';
import LoopIcon from '@material-ui/icons/Loop';
import SentimentDissatisfiedIcon from '@material-ui/icons/SentimentDissatisfied';
import CheckIcon from '@material-ui/icons/Check';
import { mdiAccountCheck, mdiEmailCheckOutline } from '@mdi/js';
// import { useHistory } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { AppContext, LayoutContext } from '../contexts';
import { DYNAMIC_LINK_LOG_IN, HERE, LOG_IN, SEND_IT_AGAIN } from '../constants';
import { setClassNameInDefns } from '../definitionHelper';
import { sendEmailVerification } from '../services/authServices';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from './AfitdTypography';
import AfitdLink from './AfitdLink';
import AfitdMdiIcon from './AfitdMdiIcon';
// import AfitdButton from './AfitdButton';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: { marginTop: 50 },
    goToMySettingsButton: { margin: 20 },
    title: { fontWeight: 'bold', fontSize: 26, marginTop: 40, marginBottom: 16, [theme.breakpoints.down('sm')]: { marginTop: 30 } },
    message: { textAlign: 'center', marginBottom: 40, [theme.breakpoints.down('sm')]: { marginBottom: 24 } },
    content: { textAlign: 'center' },
    sendEmailAgainLinkText: { marginTop: 24, fontWeight: 'bold' },
    sendEmailAgainLink: { textDecoration: 'underline', fontWeight: 'bold' },
    logInLink: { textDecoration: 'underline', fontWeight: 'bold' },
    mainIcon: { fontSize: 93, color: 'rgba(216, 216, 216, 1)' },
    iconContainer: { display: 'flex', justifyContent: 'center' },
    secondIcon: { background: 'rgba(29, 138, 139, 1)', color: '#fff', borderRadius: '50%', fontSize: 28, padding: 2, marginLeft: ' -31px', marginTop: '64px' },
    //----------------
    createAccountLink: { textDecoration: 'underline' },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        userId,
        handleClickLoginLink,
        handleClickCreateAccountLink,
    } = params;

    const defns = {
        root: { container: true, justify: 'center' },
        content: { item: true, xl: 10, lg: 10, md: 10, sm: 10, xs: 10 },
        mainMdiIcon: { color: 'page-message', style: { width: 100 } },
        title: { variant: 'h5', clsSkinnyBold: true /* clsLightBold: true */ },
        message: { variant: 'body1' },
        linkText: { variant: 'body1', component: 'p' },
        loginLinkText: { variant: 'body1', component: 'p', isHidden: !!userId },
        createAccountLinkText: { variant: 'body1', component: 'p', isHidden: !!userId },
        loginLink: { color: 'secondary', onClick: handleClickLoginLink, label: LOG_IN },
        createAccountLink: { color: 'secondary', onClick: handleClickCreateAccountLink, label: 'create an account' },
        sendEmailAgainLink: { color: 'secondary', onClick: handleClickResendButton, label: SEND_IT_AGAIN },
        logInHereLink: { color: 'secondary', onClick: handleClickLoginLink, label: HERE },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handleClickResendButton = async () => {
    await sendEmailVerification();
    alert('Email sent! It might take up to 10 minutes for it to arrive your inbox.');
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const PageMessage = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const appContext = useContext(AppContext);
    const { userId } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { setIsRequestOpenCreateAccountDialog } = layoutContext;

    // const history = useHistory();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const {
        invitationInvalidOrAccepted,
        jobSeekerInvitee,
        inviteeAccountNotExist,
        successful,
        httpsError,
        noLinkedEmployers,
        noOpenInvitation,
        allInvitationsDeclined,
        interviewJoinCodeInvalid,
        interviewNotConfirmed,
        interviewJoinCodeExpired,
        processing,
        signUpSuccessful,
        emailVerifySuccessful,
        resetEmailSentSuccessful,
        passwordResetSuccessful,
        jsAccessEmpPage,
        empAccessJsPage,
        userNoAccess,
        pageNotFound,
        surveyCodeInvalid,
        surveyCompleted,
    } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [homePageId, setHomePageId] = useState(HOMEPAGE_LANDING);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickLoginLink = () => {
        // history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_SETTINGS}`);
        window.location.href = DYNAMIC_LINK_LOG_IN;
    };

    const handleClickCreateAccountLink = () => {
        // history.push(`${BASE_URL}${PATH_HOME}`, { isPromptCreateAccount: true });
        setIsRequestOpenCreateAccountDialog(true);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, userId, handleClickLoginLink, handleClickCreateAccountLink });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <Grid {...defns.root}>
            {processing && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <LoopIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Processing....</AfitdTypography>
                    <AfitdTypography {...defns.message}>Please wait while we process your request</AfitdTypography>
                    <AfitdTypography {...defns.loginLinkText}>
                        <AfitdLink {...defns.loginLink} />
                    </AfitdTypography>
                </Grid>
            )}
            {invitationInvalidOrAccepted && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <LinkOffIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Invitation Invalid or Already Accepted</AfitdTypography>
                    <AfitdTypography {...defns.message}>The invitation for this email address is invalid or it has already been accepted.</AfitdTypography>
                    <AfitdTypography {...defns.loginLinkText}>
                        <AfitdLink {...defns.loginLink} />
                    </AfitdTypography>
                </Grid>
            )}
            {interviewJoinCodeInvalid && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <LinkOffIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Invalid Link</AfitdTypography>
                    <AfitdTypography {...defns.message}>The Interview link is invalid.</AfitdTypography>
                    <AfitdTypography {...defns.loginLinkText}>
                        <AfitdLink {...defns.loginLink} />
                    </AfitdTypography>
                </Grid>
            )}
            {interviewNotConfirmed && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <LinkOffIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Unconfirmed Interview</AfitdTypography>
                    <AfitdTypography {...defns.message}>The Interview has not been confirmed yet.</AfitdTypography>
                    <AfitdTypography {...defns.loginLinkText}>
                        <AfitdLink {...defns.loginLink} />
                    </AfitdTypography>
                </Grid>
            )}
            {interviewJoinCodeExpired && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <LinkOffIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Expired Link</AfitdTypography>
                    <AfitdTypography {...defns.message}>The Interview link has expired.</AfitdTypography>
                    <AfitdTypography {...defns.loginLinkText}>
                        <AfitdLink {...defns.loginLink} />
                    </AfitdTypography>
                </Grid>
            )}
            {jobSeekerInvitee && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <LinkOffIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Invalid Invitee</AfitdTypography>
                    <AfitdTypography {...defns.message}>This email address belongs to a Job Seeker.</AfitdTypography>
                </Grid>
            )}
            {inviteeAccountNotExist && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <PersonAddIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Welcome to A Foot in the Door</AfitdTypography>
                    <AfitdTypography {...defns.message}>
                        Please <AfitdLink {...defns.createAccountLink} />
                    </AfitdTypography>
                    {/* <AfitdTypography {...defns.createAccountLinkText}> </AfitdTypography> */}
                </Grid>
            )}
            {successful && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <LinkIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Link Successful</AfitdTypography>
                    <AfitdTypography {...defns.message}>This email address has been successfully linked to the employer.</AfitdTypography>
                    <AfitdTypography {...defns.loginLinkText}>
                        <AfitdLink {...defns.loginLink} />
                    </AfitdTypography>
                </Grid>
            )}
            {httpsError && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <ErrorIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Error</AfitdTypography>
                    <AfitdTypography {...defns.message}>There were some problems on our end. Please try again later.</AfitdTypography>
                </Grid>
            )}
            {noLinkedEmployers && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <LinkOffIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>All Invitations Declined</AfitdTypography>
                    <AfitdTypography {...defns.message}>Your account is not linked to any employers.</AfitdTypography>
                </Grid>
            )}
            {noOpenInvitation && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <LinkOffIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>No Invitations Found</AfitdTypography>
                    <AfitdTypography {...defns.message}>There is no open invitations associated to your account.</AfitdTypography>
                </Grid>
            )}
            {allInvitationsDeclined && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <LinkOffIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>All Invitations Declined</AfitdTypography>
                    <AfitdTypography {...defns.message}>There is no more invitations associated to your account.</AfitdTypography>
                </Grid>
            )}
            {signUpSuccessful && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <AfitdMdiIcon path={mdiAccountCheck} {...defns.mainMdiIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Great! You've successfully signed up</AfitdTypography>
                    <AfitdTypography {...defns.message}>
                        We've sent you a link to confirm your email address. Please check your inbox. It could take up to 10 minutes to show up in your inbox. Didn't get the email?
                        Please check your spam folder.
                        <br />
                        <br />
                        Still don't have the Email? <AfitdLink {...defns.sendEmailAgainLink} />
                    </AfitdTypography>
                </Grid>
            )}
            {emailVerifySuccessful && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <AfitdMdiIcon path={mdiEmailCheckOutline} {...defns.mainMdiIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Email Verified Successfully</AfitdTypography>
                    <AfitdTypography {...defns.message}>
                        Thank You! Your email address has been verified. We'll now use this address to send you notifications about your account.
                        <br />
                        <br />
                        Continue to Log In <AfitdLink {...defns.logInHereLink} />
                    </AfitdTypography>
                </Grid>
            )}
            {resetEmailSentSuccessful && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <MailOutlineIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Reset Link Sent Successfully</AfitdTypography>
                    <AfitdTypography {...defns.message}>
                        A link to reset your password has been emailed to you.
                        <br />
                        Please check your spam folder if you do not see our email in your inbox.
                    </AfitdTypography>
                </Grid>
            )}
            {passwordResetSuccessful && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <CheckCircleIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Password Reset Successfully</AfitdTypography>
                    <AfitdTypography {...defns.message}>Your password has been successfully reset. Please log in with your new password.</AfitdTypography>
                </Grid>
            )}
            {jsAccessEmpPage && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <BlockIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Access Denied</AfitdTypography>
                    <AfitdTypography {...defns.message}>Looks like you are trying to access an employer page. Please sign in with an employer account.</AfitdTypography>
                </Grid>
            )}
            {empAccessJsPage && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <BlockIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Access Denied</AfitdTypography>
                    <AfitdTypography {...defns.message}>Looks like you are trying to access a job seeker page. Please sign in with a job seeker account.</AfitdTypography>
                </Grid>
            )}
            {userNoAccess && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <BlockIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Access Denied</AfitdTypography>
                    <AfitdTypography {...defns.message}>You do not have access to this page.</AfitdTypography>
                </Grid>
            )}
            {pageNotFound && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <SentimentDissatisfiedIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Page Not Found</AfitdTypography>
                    <AfitdTypography {...defns.message}>The page you are looking for does not exist.</AfitdTypography>
                </Grid>
            )}
            {surveyCodeInvalid && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <LinkOffIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Invalid Survey</AfitdTypography>
                    <AfitdTypography {...defns.message}>The survey link is invalid or has expired.</AfitdTypography>
                    <AfitdTypography {...defns.loginLinkText}>
                        <AfitdLink {...defns.loginLink} />
                    </AfitdTypography>
                </Grid>
            )}
            {surveyCompleted && (
                <Grid {...defns.content}>
                    <div {...defns.iconContainer}>
                        <CheckIcon {...defns.mainIcon} />
                        {/* <CheckIcon {...defns.secondIcon} /> */}
                    </div>
                    <AfitdTypography {...defns.title}>Survey Completed</AfitdTypography>
                    <AfitdTypography {...defns.message}>This survey has been completed and we sincerely appreciate your feedback.</AfitdTypography>
                    <AfitdTypography {...defns.loginLinkText}>
                        <AfitdLink {...defns.loginLink} />
                    </AfitdTypography>
                </Grid>
            )}
        </Grid>
    );
};

export default PageMessage;
